import { Badge, Card, Table } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { Organization } from '@/api/orgs';
import { SortableTh } from '@/components/SortableTh';
import { rowClick, rowStyle } from '@/components/rowClick';
import { useFormat } from '@/components/useFormat';
import { useTableSort } from '@/components/useTableSort';
import { brand } from '@/theme/theme';

export function OrganizationsTable({ organizations }: { organizations: Organization[] }) {
  const { t } = useTranslation();
  const { sorted, sortProps } = useTableSort(organizations, {
    name: (o) => o.name,
    projects: (o) => o.projects_count,
    members: (o) => o.members_count,
    status: (o) => t(`org.status.${o.status ?? 'active'}`),
  });
  return (
    <Card p={0}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <SortableTh {...sortProps('name')}>{t('org.name')}</SortableTh>
            <SortableTh {...sortProps('projects')} ta="right">{t('org.projectsCount')}</SortableTh>
            <SortableTh {...sortProps('members')} ta="right">{t('org.membersCount')}</SortableTh>
            <SortableTh {...sortProps('status')}>{t('org.status.label')}</SortableTh>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sorted.map((org) => <OrgRow key={org.public_id} org={org} />)}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

function OrgRow({ org }: { org: Organization }) {
  const { t } = useTranslation();
  const { number } = useFormat();
  const navigate = useNavigate();
  const status = org.status ?? 'active';
  return (
    <Table.Tr style={rowStyle} onClick={rowClick(() => void navigate({ to: '/staff/organizations/$orgId', params: { orgId: org.public_id } }))}>
      <Table.Td>
        <Link
          to="/staff/organizations/$orgId"
          params={{ orgId: org.public_id }}
          style={{ fontWeight: 600, color: brand.text, textDecoration: 'none' }}
        >
          {org.name}
        </Link>
      </Table.Td>
      <Table.Td ta="right">{number(org.projects_count)}</Table.Td>
      <Table.Td ta="right">{number(org.members_count)}</Table.Td>
      <Table.Td>
        <Badge color={status === 'active' ? 'forest' : 'red'} variant="light">
          {t(`org.status.${status}`)}
        </Badge>
      </Table.Td>
    </Table.Tr>
  );
}
